import { differenceInMinutes } from 'date-fns'

import { ConvoState, TOP_FILTERS } from '.'
import { fetchSessions } from '../../lib/api'
import { timelineToMessages, timelineToNotes } from '../../utilities'

// Gets pages of sessions from a query
export const loadSessionsList = async (
  query: string,
  firstPageOnly = false
) => {
  const perPage = 100
  const loadChunk = async (page: number) => {
    return await fetchSessions(`${query}&page=${page}&per_page=${perPage}`)
  }

  // Load first page and get total amount
  const {
    data,
    pagination: { total }
  } = await loadChunk(0)
  const allSessions: APISession[] = [...data]

  // Get remaining pages
  if (!firstPageOnly && total > perPage) {
    let page = 0
    while ((page + 1) * perPage < total) {
      const { data: nextSet } = await loadChunk(page + 1)
      page++
      allSessions.push(...nextSet)
      await new Promise(res => setTimeout(res, 100))
    }
  }

  return allSessions
}

const isVisible = (session: APISession, filter: APIInboxViewFilter) => {
  if (filter.initiated_with) {
    if (!filter.initiated_with.includes(session.initiated_by)) {
      return false
    }
  }

  if (filter.state) {
    if (!filter.state.includes(session.state)) {
      return false
    }
  }

  if (filter.created_within) {
    const minuteDiff = differenceInMinutes(
      new Date(),
      new Date(session.createdAt)
    )
    if (minuteDiff > filter.created_within) {
      return false
    }
  }

  return true
}

export const isConversationInFilter = (
  session: APISession,
  topFilter: string,
  inboxID: string,
  userID: string,
  view: APIView | null
) => {
  if (session.inbox_id !== inboxID) {
    return false
  }

  if (view && !isVisible(session, view.filter)) {
    return false
  }

  switch (topFilter) {
    case TOP_FILTERS.ALL:
      return true
    case TOP_FILTERS.ASSIGNED_TO_ME:
      if (!userID) {
        return false
      }

      return session.assigned_to === userID
    case TOP_FILTERS.OFFLINE:
      return session.initiated_by === 'offline_integration'
    case TOP_FILTERS.TRANSFERS:
      if (!userID) {
        return false
      }

      // Show session that you were assigned to / assigned somemone but not if you are the current assignee
      if (session.assigned_to === userID) {
        return false
      }

      const assignHistory = session.assign_history.flatMap(history => [
        history.by,
        history.to
      ])
      return assignHistory.includes(userID)
    default:
      return false
  }
}

export const getRelated = (state: ConvoState, session: string) => {
  const baseSession = state.allConversations[session]
  if (!baseSession?.owner_id) {
    return []
  }

  // Get list of session ids with same owner as the one this message went to
  const toUpdate = Object.entries(state.allConversations)
    .filter(([_, value]) => value.owner_id === baseSession?.owner_id)
    .map(([key, _]) => key)
  return toUpdate
}

export const hydrateConversation = (
  session: APISession,
  isNew: boolean,
  setOwner?: (id: string, owner: string) => void
) => {
  // @ts-ignore
  const owner_id = (session.owner_id ?? session.ownerId) as string
  const partial = session.latest_activity

  const timeline = partial || []
  const messages = timelineToMessages(timeline)

  const notes = timelineToNotes(timeline)
  if (!session.users) {
    session.users = []
    if (setOwner) {
      setOwner(session.id, owner_id)
    }
  }
  const user = session.users.find(({ id }) => id === owner_id)

  const newSession: LocalSession = {
    ...session,
    name: session.name ?? user?.name ?? null,
    startDate: session.createdAt || new Date().toISOString(),
    endDate: session.ended_at,
    lastUpdate:
      (timeline.length > 0 && timeline[0].stamp) ||
      session.updatedAt ||
      new Date().toISOString(),
    type: 'Live',
    session: session.id,
    fetched: false,
    messages,
    notes,
    isNew,
    users: session.userIds || [],
    user: user || null,
    owner_id,
    title: session.name,
    closed: Boolean(session.ended_at),
    fullUsers: session.users
  }

  return newSession
}
